import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Phone } from "lucide-react";
import { IMAGES, LUNCH_NUDGE, PHONE_DISPLAY, PHONE_TEL, PRINCIPAL } from "@/data";
import { BookButton, CallButton } from "@/components/Buttons";
import { EASE } from "@/components/Reveal";
import { OfferPill } from "@/components/OfferPill";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const PARALLAX_SCROLL_RANGE_PX = [0, 900];
const PARALLAX_SHIFT_PX = [0, 240];
const FADE_SCROLL_RANGE_PX = [0, 500];
const STATS = [
  ["1989", "Caring for Canberra smiles since"],
  ["Same-day", "Crowns, plus conscious sedation for anxious patients"],
  ["HICAPS", "Health-fund claims on the spot"],
];

const StatsBar = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1, delay: 2 }}
    className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10"
  >
    <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-white/10 px-5 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-8 lg:px-16">
      {STATS.map(([big, small]) => (
        <div key={small} className="flex items-baseline gap-4 py-5 sm:py-6">
          <span className="font-serif text-2xl text-cream sm:text-3xl">{big}</span>
          <span className="font-sans text-xs leading-snug text-mist sm:text-sm">{small}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const HERO_FACTS = [
  ["Where", "Next to Glebe Park, Coranderrk St"],
  ["Open", "Mon–Sat · 9:00am — 5:30pm"],
  ["Health funds", "Preferred provider for HCF & nib"],
  ["New patients", "Welcome — no referral needed"],
  ["Emergencies", "Same-day slots where possible"],
  ["Sedation", "Available for anxious patients"],
];

const HeroCard = () => (
  <motion.aside
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay: 1.3, ease: EASE }}
    className="hidden lg:col-span-5 lg:block"
    data-testid="hero-info-card"
  >
    <div className="rounded-2xl border border-cream/15 bg-cream/[0.07] p-8 shadow-[0_30px_70px_-35px_rgba(0,0,0,0.7)] backdrop-blur-xl">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-leaf">At a glance</p>
      <p className="mt-3 font-sans text-sm leading-relaxed text-mist">Everything you need to know before your first visit.</p>
      <dl className="mt-5 divide-y divide-cream/10">
        {HERO_FACTS.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-6 py-[0.95rem]">
            <dt className="font-sans text-sm text-mist">{k}</dt>
            <dd className="max-w-[62%] text-right font-sans text-sm text-cream">{v}</dd>
          </div>
        ))}
      </dl>
      <a
        href={PHONE_TEL}
        data-testid="hero-card-phone-link"
        className="mt-7 flex items-center justify-center gap-2 rounded-md border border-cream/20 py-3 font-sans text-sm text-cream transition-colors duration-300 hover:border-cream hover:bg-cream hover:text-forest"
      >
        <Phone className="h-4 w-4" />
        {PHONE_DISPLAY}
      </a>
    </div>
  </motion.aside>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, PARALLAX_SCROLL_RANGE_PX, PARALLAX_SHIFT_PX);
  const fade = useTransform(scrollY, FADE_SCROLL_RANGE_PX, [1, 0]);

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden bg-forest">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Treatment room at Glebe Park Dental, Canberra City"
          className="h-full w-full object-cover opacity-[0.55]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/80 to-forest/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-transparent to-forest/40" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-5 pb-28 pt-40 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-12 lg:px-16">
        <div className="lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-leaf" />
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-leaf">
            Dentist · Coranderrk St, Canberra City
          </p>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-serif text-4xl leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.65}>The city dentist</MaskedLine>
          <MaskedLine delay={0.78}>you'll actually</MaskedLine>
          <MaskedLine delay={0.91}>
            <span className="italic text-stone">look forward to.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
          className="mt-8 max-w-xl font-sans text-base leading-relaxed text-mist sm:text-lg"
        >
          We're a small practice on Coranderrk Street, right next to the
          park. {PRINCIPAL}, Dr Peter Stejskal and the team do everything from
          check-ups to implants and clear aligners — and we'll always tell you
          what's going on in plain English. No lectures, no upselling, no
          surprises on the bill.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <BookButton variant="light" testid="hero-book-appointment-button">Book online</BookButton>
          <CallButton testid="hero-call-clinic-button" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.55, ease: EASE }}
          className="mt-6"
        >
          <OfferPill testid="hero-offer-pill" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4"
        >
          <div className="flex items-center gap-3">
            <span className="pulse-dot h-2 w-2 rounded-full bg-leaf" />
            <span className="font-sans text-sm text-cream/90">New patients welcome — no referral needed</span>
          </div>
          <span data-testid="hero-lunch-nudge" className="font-sans text-sm text-mist">
            {LUNCH_NUDGE}
          </span>
        </motion.div>
        </div>

        <HeroCard />
      </motion.div>

      <StatsBar />

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-28 right-8 z-10 hidden text-leaf/70 lg:block"
      >
        <ChevronDown className="h-6 w-6" />
      </motion.div>
    </section>
  );
};

export default Hero;
